import MainLayout from 'src/layouts/MainLayout';
import { lazy } from 'react';
import { RouteI } from 'src/models/router';
import { RoutePath } from 'src/enums/routePath';
import UserNotLogin from 'src/layouts/UserNotLogin';
import CustomerLayout from 'src/layouts/Customer';
import { AuthRole } from 'src/enums/auth';
import ConfigController from 'src/pages/admin/Config/components/controller';
import { PermissionList } from 'src/enums/permission';

const ConfigPage = import('../pages/admin/Config');
const ExpirationPage = import('../pages/admin/Expiration');
const AdminLoginPage = import('../pages/admin/Login');
const CustomerLoginPage = import('../pages/customer/Login');
const AdminInsuranceApplicationCreate = import(
  '../pages/admin/SubmitAgreement/create'
);
const AdminInsuranceApplicationEdit = import(
  '../pages/admin/SubmitAgreement/edit'
);
const AdminInsuranceApplication = import('../pages/admin/SubmitAgreement');
const AdminAccount = import('../pages/admin/Account');
const AdminCreateAccount = import('../pages/admin/Account/create');
const AdminCustomerList = import('../pages/admin/Customers');
const AdminDashboard = import('../pages/admin/Dashboard');
const AdminEditAccount = import('../pages/admin/Account/edit');
const AdminAccountDetail = import('../pages/admin/Account/detail');
const AdminCreateCustomer = import('../pages/admin/Customers/create');
const AdminEditCustomer = import('../pages/admin/Customers/edit');
const AdminDetailCustomer = import('../pages/admin/Customers/detail');
const ForgotPasswordPage = import('../pages/customer/ForgotPassword');
const ResetPasswordPage = import('../pages/customer/ResetPassword');
const CustomerAgreementDetailPage = import(
  '../pages/customer/InsuranceApplicationConfirm/logged-in-pages/AgreementDetail'
);
const PublicInsuranceConfirmPendingPage = import(
  '../pages/customer/InsuranceApplicationConfirm/public-pages/confirm-pending'
);
const PublicInsuranceConfirmSuccessPage = import(
  '../pages/customer/InsuranceApplicationConfirm/public-pages/confirm-success'
);
const CustomerAgreementListPage = import(
  '../pages/customer/InsuranceApplicationConfirm/logged-in-pages/AgreementList'
);
const PublicImeiConfirmPendingPage = import(
  '../pages/customer/Imei/public-pages/confirm-pending'
);
const PublicImeiConfirmSuccessPage = import(
  '../pages/customer/Imei/public-pages/success'
);
const PlanRegistrationPage = import(
  '../pages/customer/EnrolledPlanManagement/public-pages/PlanRegistration'
);
const PublicStorePayRegistrationSuccessPage = import(
  '../pages/customer/EnrolledPlanManagement/public-pages/StoreRegisterSuccess'
);
const PaymentPage = import(
  '../pages/customer/EnrolledPlanManagement/public-pages/Payment'
);
const CustomerImeiListPage = import(
  '../pages/customer/Imei/logged-in-pages/ImeiList'
);
const CustomerImeiConfirmPage = import(
  '../pages/customer/Imei/logged-in-pages/Confirm'
);
const CustomerImeiHistoryDetailPage = import(
  '../pages/customer/Imei/logged-in-pages/ImeiHistoryDetail'
);
const AdminRepairList = import('../pages/admin/Repairs');
const RepairCreatePage = import('../pages/admin/Repairs/create');
const RepairEditPage = import('../pages/admin/Repairs/edit');
const RepairDetailPage = import('../pages/admin/Repairs/detail');
const MakerList = import('../pages/admin/Makers');
const ProductCategoryList = import('../pages/admin/ProductCategory');
const ModelList = import('../pages/admin/Models');
const CustomerHistoryListPage = import(
  '../pages/customer/InsuranceApplicationConfirm/logged-in-pages/History/HistoryList'
);
const CustomerHistoryDetailPage = import(
  '../pages/customer/InsuranceApplicationConfirm/logged-in-pages/History/HistoryDetail'
);
const CustomerPersonalInfoPage = import('../pages/customer/PersonalInfo');
const AdminClientList = import('../pages/admin/Clients');
const AdminCreateClientPage = import('../pages/admin/Clients/create');
const AdminEditClientPage = import('../pages/admin/Clients/edit');
const AdminClientDetailPage = import('../pages/admin/Clients/detail');
const ImeiCreatePage = import('../pages/admin/Imei/create');
const PlanListPage = import('../pages/admin/Plans');
const PlanCreatePage = import('../pages/admin/Plans/create');
const PlanEditPage = import('../pages/admin/Plans/edit');
const PlanDetailPage = import('../pages/admin/Plans/detail');
const CustomerEnrolledPlanListPage = import(
  '../pages/customer/EnrolledPlanManagement/PlanList'
);
const CustomerEnrolledPlanDetailPage = import(
  '../pages/customer/EnrolledPlanManagement/PlanDetail'
);
const PaymentListPage = import('../pages/admin/Payments');
const SalesManagementPage = import('../pages/admin/Sales');
const AgencyListPage = import('../pages/admin/Agency');
const AgencyDetailPage = import('../pages/admin/Agency/detail');
const AgencyEditPage = import('../pages/admin/Agency/edit');
const AgencyCreatePage = import('../pages/admin/Agency/create');
const CustomerUpdateCardStatusPage = import(
  '../pages/customer/EnrolledPlanManagement/UpdateCard'
);
const AgencyAuthorityCreatePage = import('../pages/admin/AgencyAuthority');
const AgencyAuthorityEditPage = import('../pages/admin/AgencyAuthority/edit');
const AgencyAuthorityDetailPage = import(
  '../pages/admin/AgencyAuthority/detail'
);
const AdminForgotPasswordPage = import('../pages/admin/ForgotPassword');
const AdminResetPasswordPage = import('../pages/admin/ResetPassword');
const ViewDocumentsOfPlanPage = import('../pages/public/ViewDocuments');
const QRCodesPage = import('../pages/admin/QRCodes');
const CreateQRInformation = import('../pages/admin/QRCodes/create');
const DashboardPage = import('../pages/admin/Dashboard');
const CountSettingNumberPage = import('../pages/admin/CountSettingNumber');

export const routes: RouteI[] = [
  {
    path: RoutePath.ADMIN + RoutePath.LOGIN,
    Component: lazy(() => AdminLoginPage)
  },
  {
    path: RoutePath.EMPTY,
    Component: () => null,
    Layout: <MainLayout />,
    isPrivate: true,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN,
    Component: () => null,
    Layout: <MainLayout />,
    isPrivate: true,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.FORGOT_PASSWORD,
    Component: lazy(() => ForgotPasswordPage)
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.RESET_PASSWORD,
    Component: lazy(() => ResetPasswordPage)
  },
  {
    path: RoutePath.ADMIN + RoutePath.ACCOUNT,
    Component: lazy(() => AdminAccount),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.ACCOUNT_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.ACCOUNT + RoutePath.CREATE,
    Component: lazy(() => AdminCreateAccount),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.ACCOUNT_CREATE,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CUSTOMERS + RoutePath.CREATE,
    Component: lazy(() => AdminCreateCustomer),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.CUSTOMER_CREATE,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CUSTOMERS + RoutePath.ID + RoutePath.EDIT,
    Component: lazy(() => AdminEditCustomer),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.CUSTOMER_EDIT,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CUSTOMERS + RoutePath.ID,
    Component: lazy(() => AdminDetailCustomer),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.CUSTOMER_DETAIL,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.ACCOUNT + RoutePath.ID,
    Component: lazy(() => AdminAccountDetail),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.ACCOUNT_DETAIL,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.ACCOUNT + RoutePath.ID + RoutePath.EDIT,
    Component: lazy(() => AdminEditAccount),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.ACCOUNT_EDIT,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.INSURANCE_APPLICATION + RoutePath.CREATE,
    Component: lazy(() => AdminInsuranceApplicationCreate),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.SUBMIT_AGREEMENT_CREATE,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.INSURANCE_APPLICATION,
    Component: lazy(() => AdminInsuranceApplication),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.SUBMIT_AGREEMENT_LIST,
    role: AuthRole.ADMIN
  },
  {
    path:
      RoutePath.ADMIN +
      RoutePath.INSURANCE_APPLICATION +
      RoutePath.ID +
      RoutePath.EDIT,
    Component: lazy(() => AdminInsuranceApplicationEdit),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.SUBMIT_AGREEMENT_EDIT,
    role: AuthRole.ADMIN
  },
  {
    path:
      RoutePath.CUSTOMER +
      RoutePath.INSURANCE_APPLICATION +
      RoutePath.HASH_CODE,
    isPrivate: true,
    Layout: <CustomerLayout />,
    Component: lazy(() => CustomerAgreementDetailPage),
    role: AuthRole.CUSTOMER
  },
  {
    path: RoutePath.CUSTOMER + '/update-card',
    isPrivate: true,
    Layout: <CustomerLayout />,
    Component: lazy(() => CustomerUpdateCardStatusPage),
    role: AuthRole.CUSTOMER
  },
  {
    path: RoutePath.REQUEST + RoutePath.HASH_CODE,
    Layout: <UserNotLogin />,
    Component: lazy(() => PublicInsuranceConfirmPendingPage)
  },
  {
    path: RoutePath.REQUEST + RoutePath.ID + RoutePath.SUCCESS,
    Component: lazy(() => PublicInsuranceConfirmSuccessPage)
  },
  {
    path: RoutePath.ADMIN + RoutePath.CUSTOMERS,
    Component: lazy(() => AdminCustomerList),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.CUSTOMER_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.LOGIN,
    Component: lazy(() => CustomerLoginPage)
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.INSURANCE_APPLICATION,
    Component: lazy(() => CustomerAgreementListPage),
    isPrivate: true,
    role: AuthRole.CUSTOMER,
    Layout: <CustomerLayout />
  },
  {
    path: RoutePath.REQUEST + RoutePath.IMEI + RoutePath.HASH_CODE,
    Layout: <UserNotLogin />,
    Component: lazy(() => PublicImeiConfirmPendingPage)
  },
  {
    path:
      RoutePath.REQUEST +
      RoutePath.IMEI +
      RoutePath.HASH_CODE +
      RoutePath.SUCCESS,
    Layout: <UserNotLogin />,
    Component: lazy(() => PublicImeiConfirmSuccessPage)
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.REGISTER,
    Layout: <UserNotLogin hasHeader={false} />,
    Component: lazy(() => PlanRegistrationPage)
  },

  {
    path: RoutePath.CUSTOMER + RoutePath.REGISTER + RoutePath.SUCCESS,
    Component: lazy(() => PublicStorePayRegistrationSuccessPage)
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.IMEI,
    Component: lazy(() => CustomerImeiListPage),
    isPrivate: true,
    role: AuthRole.CUSTOMER,
    Layout: <CustomerLayout />
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.IMEI + RoutePath.ID,
    Component: lazy(() => CustomerImeiConfirmPage),
    isPrivate: true,
    role: AuthRole.CUSTOMER,
    Layout: <CustomerLayout />
  },
  {
    path:
      RoutePath.CUSTOMER +
      RoutePath.IMEI +
      RoutePath.HISTORY_DETAIL +
      RoutePath.ID,
    Component: lazy(() => CustomerImeiHistoryDetailPage),
    isPrivate: true,
    role: AuthRole.CUSTOMER,
    Layout: <CustomerLayout />
  },
  {
    path: RoutePath.CUSTOMER_HISTORY,
    Component: lazy(() => CustomerHistoryListPage),
    isPrivate: true,
    role: AuthRole.CUSTOMER,
    Layout: <CustomerLayout />
  },
  {
    path: RoutePath.CUSTOMER_HISTORY + RoutePath.ID,
    Component: lazy(() => CustomerHistoryDetailPage),
    isPrivate: true,
    role: AuthRole.CUSTOMER,
    Layout: <CustomerLayout />
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.SUBSCRIPTIONS,
    Component: lazy(() => CustomerEnrolledPlanListPage),
    isPrivate: true,
    role: AuthRole.CUSTOMER,
    Layout: <CustomerLayout />
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.SUBSCRIPTIONS_DETAIL,
    Component: lazy(() => CustomerEnrolledPlanDetailPage),
    isPrivate: true,
    role: AuthRole.CUSTOMER,
    Layout: <CustomerLayout />
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.PERSONAL_INFO,
    Component: lazy(() => CustomerPersonalInfoPage),
    isPrivate: true,
    role: AuthRole.CUSTOMER,
    Layout: <CustomerLayout />
  },
  {
    path: RoutePath.CUSTOMER + RoutePath.PAYMENT,
    Layout: <UserNotLogin hasHeader={false} />,
    Component: lazy(() => PaymentPage)
  },
  {
    path: RoutePath.REPAIRS,
    Component: lazy(() => AdminRepairList),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.REPAIR_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.REPAIRS_CREATE,
    Component: lazy(() => RepairCreatePage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.REPAIR_CREATE,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.REPAIRS_EDIT,
    Component: lazy(() => RepairEditPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.REPAIR_EDIT,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.REPAIRS_DETAIL,
    Component: lazy(() => RepairDetailPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.REPAIR_DETAIL,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.MAKERS,
    Component: lazy(() => MakerList),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.MAKER_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.PRODUCTS,
    Component: lazy(() => ProductCategoryList),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.CATEGORY_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.MODELS,
    Component: lazy(() => ModelList),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.MODEL_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CONFIG,
    Component: lazy(() => ConfigPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.PERMISSION_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CONFIG + RoutePath.EXPIRATION,
    Component: lazy(() => ExpirationPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.ENABLING_REPAIR_AGREEMENT_LINK,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CONFIG + RoutePath.CREATE,
    Component: ConfigController,
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.PERMISSION_CREATE,
    role: AuthRole.ADMIN
  },

  {
    path: RoutePath.ADMIN + RoutePath.CONFIG + RoutePath.ID + RoutePath.EDIT,
    Component: ConfigController,
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.PERMISSION_EDIT,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CLIENTS,
    Component: lazy(() => AdminClientList),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.CLIENT_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CLIENTS + RoutePath.CREATE,
    Component: lazy(() => AdminCreateClientPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.CLIENT_CREATE,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CLIENTS + RoutePath.ID + RoutePath.EDIT,
    Component: lazy(() => AdminEditClientPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.CLIENT_EDIT,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.CLIENTS + RoutePath.ID,
    Component: lazy(() => AdminClientDetailPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.CLIENT_DETAIL,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.IMEI + RoutePath.CREATE,
    Component: lazy(() => ImeiCreatePage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.IMEI_CREATE,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.PLANS,
    Component: lazy(() => PlanListPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.PLAN_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.PLAN_CREATE,
    Component: lazy(() => PlanCreatePage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.PLAN_CREATE,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.PLAN_EDIT,
    Component: lazy(() => PlanEditPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.PLAN_EDIT,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.PLAN_DETAIL,
    Component: lazy(() => PlanDetailPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.PLAN_DETAIL,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.PAYMENTS,
    Component: lazy(() => PaymentListPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.PAYMENT_MANAGER_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.SALES,
    Component: lazy(() => SalesManagementPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.SALES_MANAGER_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.AGENCY,
    Component: lazy(() => AgencyListPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.AGENCY_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.AGENCY + RoutePath.CREATE,
    Component: lazy(() => AgencyCreatePage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.AGENCY_CREATE,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.AGENCY_EDIT,
    Component: lazy(() => AgencyEditPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.AGENCY_EDIT,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.AGENCY + RoutePath.ID,
    Component: lazy(() => AgencyDetailPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.AGENCY_DETAIL,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.AGENCY_AUTHORITY,
    Component: lazy(() => AgencyAuthorityCreatePage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.AGENCY_AUTHORITY_LIST,
    role: AuthRole.ADMIN
  },
  {
    path:
      RoutePath.ADMIN +
      RoutePath.AGENCY_AUTHORITY +
      RoutePath.ID +
      RoutePath.EDIT,
    Component: lazy(() => AgencyAuthorityEditPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.AGENCY_AUTHORITY_EDIT,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.AGENCY_AUTHORITY + RoutePath.ID,
    Component: lazy(() => AgencyAuthorityDetailPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.AGENCY_AUTHORITY_DETAIL,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.FORGOT_PASSWORD,
    Component: lazy(() => AdminForgotPasswordPage)
  },
  {
    path: RoutePath.ADMIN + RoutePath.RESET_PASSWORD,
    Component: lazy(() => AdminResetPasswordPage)
  },
  {
    path: RoutePath.DOCUMENT_VIEW,
    Component: lazy(() => ViewDocumentsOfPlanPage)
  },
  {
    path: RoutePath.ADMIN + RoutePath.QR_CODE,
    Component: lazy(() => QRCodesPage),
    Layout: <MainLayout />,
    isPrivate: true,
    requirePermission: PermissionList.QR_REGISTER_LIST,
    role: AuthRole.ADMIN
  },
  {
    path: RoutePath.ADMIN + RoutePath.QR_CODE + RoutePath.CREATE,
    Layout: <MainLayout />,
    Component: lazy(() => CreateQRInformation),
    isPrivate: true,
    requirePermission: PermissionList.QR_REGISTER_CREATE,
    role: AuthRole.ADMIN
  },
  // {
  //   path: RoutePath.ADMIN + RoutePath.DASHBOARD,
  //   Layout: <MainLayout />,
  //   Component: lazy(() => DashboardPage),
  //   isPrivate: true,
  //   requirePermission: PermissionList.QR_REGISTER_CREATE,
  //   role: AuthRole.ADMIN
  // },
  // {
  //   path: RoutePath.ADMIN + RoutePath.COUNT_SETTING_NUMBER,
  //   Layout: <MainLayout />,
  //   Component: lazy(() => CountSettingNumberPage),
  //   isPrivate: true,
  //   requireAnyPermission: [
  //     PermissionList.COUNT_CALLS_LIST,
  //     PermissionList.COUNT_REPAIRS_LIST
  //   ],
  //   role: AuthRole.ADMIN
  // }
];
